import type { ReadOnlyCloudCliFix, ReadOnlyCodeFix, ReadOnlyYamlFix } from './ReadOnlyFixCards';

// A workflow RCA step output, as produced by triggerRCA / triggerCloudRCA in
// server/pkg/server/workflow_integration_handlers.go.
export interface WorkflowRCAStepOutput {
  rca_result?: any;
  fixes?: any[];
  status?: string;
  [key: string]: unknown;
}

export interface ReadOnlyDetectionSignal {
  signal_type?: string;
  reason?: string;
  message?: string;
  timestamp?: number | string;
}

export interface AdaptedRCAView {
  source: 'k8s' | 'cloud';
  rca: any;
  severity: string;
  incidentType: string;
  clusterName?: string;
  clusterId?: string;
  accountId?: string;
  detectedAt?: string;
  completedAt?: string;
  affectedResources: any[];
  detectionSignals: ReadOnlyDetectionSignal[];
  yamlFixes: ReadOnlyYamlFix[];
  codeFixes: ReadOnlyCodeFix[];
  cloudCliFixes: ReadOnlyCloudCliFix[];
  terraformFixes: Array<{ file_path: string; content: string; explanation?: string }>;
}

// Detect whether a step targets the cloud RCA agent.
export function isCloudRCAStep(action?: string, rca?: any): boolean {
  if (action === 'kestrel-trigger-cloud-rca') return true;
  if (!rca) return false;
  // Cloud RCA results carry AWS/OCI-style fix arrays directly on rca_result.
  return (
    Array.isArray(rca.api_fixes) ||
    Array.isArray(rca.terraform_fixes) ||
    Array.isArray(rca.oci_cli_fixes) ||
    typeof rca.aws_account_id === 'string' ||
    typeof rca.oci_tenancy_ocid === 'string'
  );
}

// Map a K8s YAML fix (step-output shape) to the read-only YAML fix card shape.
// Step output uses {type, description, resource, resource_name, resource_namespace,
// resource_kind, file_type, yaml_content, existing_yaml}.
function toYamlFix(fix: any): ReadOnlyYamlFix {
  // Older executions stored `resource` built via Go's fmt with nil values,
  // yielding "%!s(<nil>)/%!s(<nil>)". Treat any such value as missing so we fall
  // back to a sensible path instead of rendering the formatting sentinel.
  const cleanResource =
    typeof fix.resource === 'string' && !fix.resource.includes('%!s(<nil>)')
      ? fix.resource
      : undefined;
  return {
    file_path:
      fix.file_path ||
      cleanResource ||
      [fix.resource_namespace, fix.resource_name].filter(Boolean).join('/') ||
      fix.resource_name ||
      'manifest.yaml',
    file_type: fix.file_type || 'update',
    yaml_content: fix.yaml_content || '',
    existing_yaml: fix.existing_yaml || undefined,
    explanation: fix.explanation || fix.description || '',
    resource_name: fix.resource_name || undefined,
    resource_kind: fix.resource_kind || undefined,
    resource_namespace: fix.resource_namespace || undefined,
  };
}

// Map a code fix (step-output shape) to the read-only code fix card shape.
// Step output uses {type:"code", description, file_path, original_code, fixed_code,
// repo_id, line_range}. line_range is typically an [start, end] int array (as the
// server sends it), but may also arrive as a "start-end" string.
function toCodeFix(fix: any): ReadOnlyCodeFix {
  let lineStart = Number(fix.line_start) || 0;
  let lineEnd = Number(fix.line_end) || 0;
  if (!lineStart || !lineEnd) {
    if (Array.isArray(fix.line_range)) {
      const start = Number(fix.line_range[0]);
      const end = Number(fix.line_range[1]);
      if (!Number.isNaN(start) && start > 0) lineStart = start;
      if (!Number.isNaN(end) && end > 0) lineEnd = end;
    } else if (typeof fix.line_range === 'string') {
      const parts = fix.line_range.split('-').map((p: string) => parseInt(p.trim(), 10));
      if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        lineStart = parts[0];
        lineEnd = parts[1];
      }
    }
  }
  return {
    file_path: fix.file_path || 'source',
    file_type: fix.file_type || 'update',
    original_code: fix.original_code || undefined,
    fixed_code: fix.fixed_code || '',
    explanation: fix.explanation || fix.description || '',
    line_start: lineStart,
    line_end: lineEnd,
    language: fix.language || inferLanguage(fix.file_path || ''),
  };
}

function inferLanguage(filePath: string): string {
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  switch (ext) {
    case 'py':
      return 'python';
    case 'go':
      return 'go';
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'java':
      return 'java';
    case 'rs':
      return 'rust';
    default:
      return ext || 'text';
  }
}

// Map a cloud CLI fix (rca_result.api_fixes / oci_cli_fixes raw agent shape) to the
// read-only cloud CLI fix card shape.
function toCloudCliFix(fix: any): ReadOnlyCloudCliFix {
  return {
    service: fix.service || undefined,
    operation: fix.operation || undefined,
    explanation: fix.explanation || fix.description || undefined,
    cli_command: fix.cli_command || undefined,
    rollback_command: fix.rollback_command || undefined,
    risk_level: fix.risk_level || undefined,
    resource_arn: fix.resource_arn || undefined,
    resource_ocid: fix.resource_ocid || undefined,
  };
}

// Adapt a workflow RCA step output into the read-only incident view shape.
export function adaptWorkflowRCA(
  output: WorkflowRCAStepOutput,
  action?: string,
  meta?: { startedAt?: string; completedAt?: string; clusterName?: string; clusterId?: string; triggerSignal?: any },
): AdaptedRCAView {
  const rca = output.rca_result || {};
  const stepFixes = Array.isArray(output.fixes) ? output.fixes : [];
  const cloud = isCloudRCAStep(action, rca);

  const yamlFixes: ReadOnlyYamlFix[] = [];
  const codeFixes: ReadOnlyCodeFix[] = [];
  const cloudCliFixes: ReadOnlyCloudCliFix[] = [];
  const terraformFixes: Array<{ file_path: string; content: string; explanation?: string }> = [];

  if (cloud) {
    // Prefer the richer raw fix arrays on rca_result; fall back to top-level fixes.
    const apiFixes: any[] = Array.isArray(rca.api_fixes)
      ? rca.api_fixes
      : Array.isArray(rca.oci_cli_fixes)
        ? rca.oci_cli_fixes
        : [];
    if (apiFixes.length > 0) {
      apiFixes.forEach((f) => cloudCliFixes.push(toCloudCliFix(f)));
    } else {
      stepFixes
        .filter((f) => f.type === 'aws_api' || f.type === 'oci_cli' || f.cli_command)
        .forEach((f) => cloudCliFixes.push(toCloudCliFix(f)));
    }

    const tfFixes: any[] = Array.isArray(rca.terraform_fixes)
      ? rca.terraform_fixes
      : stepFixes.filter((f) => f.type === 'terraform');
    tfFixes.forEach((f) =>
      terraformFixes.push({
        file_path: f.file_path || 'main.tf',
        content: f.content || '',
        explanation: f.explanation || f.description || undefined,
      }),
    );

    // Cloud RCA may still surface application-level code fixes.
    stepFixes.filter((f) => f.type === 'code').forEach((f) => codeFixes.push(toCodeFix(f)));
  } else {
    stepFixes.forEach((f) => {
      if (f.type === 'code') {
        codeFixes.push(toCodeFix(f));
      } else {
        yamlFixes.push(toYamlFix(f));
      }
    });
  }

  // Detection signals are stored on rca_result by the backend. Fall back to the
  // execution's trigger signal so older executions still show what fired.
  const rawSignals: any[] = Array.isArray(rca.detection_signals) && rca.detection_signals.length > 0
    ? rca.detection_signals
    : meta?.triggerSignal
      ? [meta.triggerSignal]
      : [];
  const detectionSignals: ReadOnlyDetectionSignal[] = rawSignals.map((s) => ({
    signal_type: s.signal_type || s.signalType || undefined,
    reason: s.reason || s.event_name || undefined,
    message: s.message || undefined,
    timestamp: s.timestamp ?? s.detected_at ?? undefined,
  }));

  return {
    source: cloud ? 'cloud' : 'k8s',
    rca,
    severity: rca.severity || 'medium',
    incidentType: rca.incident_type || 'Unknown',
    clusterName: meta?.clusterName || rca.cluster_name || undefined,
    clusterId: meta?.clusterId || rca.cluster_id || undefined,
    accountId: rca.aws_account_id || rca.oci_tenancy_ocid || undefined,
    detectedAt: meta?.startedAt || undefined,
    completedAt: meta?.completedAt || undefined,
    affectedResources: Array.isArray(rca.affected_resources) ? rca.affected_resources : [],
    detectionSignals,
    yamlFixes,
    codeFixes,
    cloudCliFixes,
    terraformFixes,
  };
}
